import React, { useEffect, useState, useCallback, useReducer } from 'react';
import PropTypes from 'prop-types';
import QuestionListCard from './QuestionListCard';
import { useDebounce } from 'utils/customHooks';
import { initialState, reducer } from 'features/questionnaires/reducers/questions/containerReducer';
import { questionValidator } from 'features/questionnaires/validators';
import { useValidation } from '@totalsoft/pure-validations-react';
import { QUESTIONTYPES_QUERY } from 'features/questionnaires/queries';
import { QUESTION_DELETE, QUESTION_SAVE, EVALUATE_QUESTION_FORMULA } from 'features/questionnaires/mutations';
import { QUESTION_DELETE_SUBSCRIPTION } from 'features/questionnaires/subscriptions';
import { useApolloClient } from '@apollo/client';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';
import { useTranslation } from 'react-i18next';

const rowsPerPageOptions = [5, 10, 20];
const defaultPagination = { page: 0, pageSize: 5 };

const QuestionsContainer = ({ questionnaireId, data, isReadOnly }) => {
  const client = useApolloClient();
  const { t } = useTranslation();
  const addToast = useToast();

  const [questionFilter, setQuestionFilter] = useState('');
  const [pagination, setPagination] = useState(defaultPagination);
  const [localState, dispatch] = useReducer(reducer, initialState);

  const [validation, validate] = useValidation(questionValidator);
  const debouncedFilterValue = useDebounce(questionFilter, 250);

  const handleChangeFilterValue = filterValue => {
    setQuestionFilter(filterValue);
  };

  const { data: questionTypesData } = useQuery(QUESTIONTYPES_QUERY, {
    skip: !questionnaireId
  });

  const [QuestionSave] = useMutation(QUESTION_SAVE);

  const [EvaluateQuestionFormula] = useMutation(EVALUATE_QUESTION_FORMULA);

  const [QuestionDelete] = useMutation(QUESTION_DELETE);

  useSubscription(QUESTION_DELETE_SUBSCRIPTION, {
    onData: () => {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'questionnaire' });
      client.cache.gc();
      addToast(t('Questionnaire.Question.QuestionDeleted'), 'success');
    }
  });

  const getItemsPerPage = useCallback((questions, pageSize, page, filter) => {
    let result;
    if (filter !== '') {
      result = filterQuestionsByValue(questions, filter);
    } else {
      const current = page * pageSize;
      result = questions.slice(current, current + pageSize);
    }
    return result;
  }, []);

  const filterQuestionsByValue = (questions, filter) => {
    if (filter === '') return questions;
    return questions.filter(o =>
      Object.keys(o).some(k => {
        if (o[k] !== null && (k === 'questionName' || k === 'questionCode'))
          return o[k].toString().toLowerCase().includes(filter.toString().toLowerCase());
        return null;
      })
    );
  };

  const handlePageChange = page => {
    setPagination({ ...pagination, page });
  };

  const handleRowsPerPageChange = size => {
    setPagination({ ...pagination, pageSize: size, page: defaultPagination.page });
  };

  const handlePaginationAndFiltersRefresh = () => {
    setPagination({ ...defaultPagination });
  };

  const handleAddNewQuestion = () => {
    dispatch({
      type: 'OnQuestionAdded',
      index: pagination.pageSize * pagination.page,
      questionnaireId
    });
  };

  const handlePropertyChanged = (detailPropertyName, detailValue, index) => {
    dispatch({ type: 'OnDetailsPropertyChanged', detailPropertyName, detailValue, index });
  };

  const handleAddNewQuestionDetailRow = (questionIndex, questionId) => () => {
    dispatch({
      type: 'OnQuestionDetailAdded',
      index: questionIndex,
      questionId: questionId
    });
  };

  const handleDetailPropertyChanged = (propName, propValue, questionIndex, questionDetailIndex) => {
    dispatch({ type: 'OnQuestionDetailChanged', propName, propValue, questionIndex, questionDetailIndex });
  };

  const handleDeleteQuestionDetailRow = (questionIndex, questionDetailIndex) => () => {
    if (window.confirm(t('Questionnaire.DeleteQuestionDetail')))
      dispatch({ type: 'OnQuestionDetailDeleted', index: questionIndex, questionDetailIndex });
  };

  const getIndexInFilteredList = index => {
    return pagination.pageSize * pagination.page + index;
  };

  const handleExpandChange = (isExpanded, index) => {
    dispatch({ type: 'OnQuestionExpanded', index: getIndexInFilteredList(index), isExpanded });
  };

  const handleDelete = index => () => {
    if (window.confirm(t('Questionnaire.DeleteQuestion'))) {
      const { questionId } = localState.questions[index];
      if (questionId) QuestionDelete({ variables: { questionId } });
      else dispatch({ type: 'OnQuestionDeleted', index });
    }
  };

  const handleSave = index => async () => {
    // eslint-disable-next-line no-unused-vars
    const { changed, isExpanded, questionActionTypes, chQuestionId, chQuestionSiteId, generationId, ...question } =
      localState.questions[index];
    const details = question.questionDetails.map(x => {
      // eslint-disable-next-line no-unused-vars
      const { chQuestionDetailId, chQuestionDetailSiteId, generationId, ...detail } = x;
      return detail;
    });
    const saveInput = {
      variables: {
        input: {
          ...question,
          questionDetails: details
        }
      }
    };

    const evaluationResult = await EvaluateQuestionFormula(saveInput);
    if (evaluationResult.data?.evaluateQuestionFormula) {
      const result = await QuestionSave(saveInput);
      if (result.data.question.isSaved) {
        client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'questionnaire' });
        client.cache.gc();
        dispatch({
          type: 'OnQuestionSaved',
          data: { id: result.data.question.questionId, idx: data.idx }
        });
      }
    } else {
      addToast(t('Questionnaire.QuestionVisibilityFormulaNotOk'), 'error');
    }
  };

  useEffect(() => {
    if (data) {
      dispatch({ type: 'OnInitState', data });
      setPagination({ ...defaultPagination });
    }
  }, [data]);

  useEffect(() => {
    validate(localState.questions);
  }, [localState.questions, validate]);

  return (
    <QuestionListCard
      questions={getItemsPerPage(localState.questions, pagination.pageSize, pagination.page, debouncedFilterValue)}
      questionTypes={questionTypesData ? questionTypesData.questionTypes : []}
      onSave={handleSave}
      validation={validation}
      isReadOnly={isReadOnly}
      pagination={pagination}
      totalCount={filterQuestionsByValue(localState.questions, debouncedFilterValue).length}
      fullTextFilter={questionFilter}
      onChangeFilterValue={handleChangeFilterValue}
      onRowsPerPageChange={handleRowsPerPageChange}
      onPageChange={handlePageChange}
      onRefresh={handlePaginationAndFiltersRefresh}
      onAddNewQuestion={handleAddNewQuestion}
      rowsPerPageOptions={rowsPerPageOptions}
      onPropertyChanged={handlePropertyChanged}
      onAddNewQuestionDetailRow={handleAddNewQuestionDetailRow}
      getIndexInFilteredList={getIndexInFilteredList}
      onDetailPropertyChanged={handleDetailPropertyChanged}
      onDeleteQuestionDetailRow={handleDeleteQuestionDetailRow}
      onExpandChange={handleExpandChange}
      onDelete={handleDelete}
    />
  );
};

QuestionsContainer.propTypes = {
  questionnaireId: PropTypes.number.isRequired,
  data: PropTypes.array.isRequired,
  isReadOnly: PropTypes.bool
};

export default QuestionsContainer;
